import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertCircle } from "lucide-react";

export const Route = createFileRoute("/ficha/$slug")({
  component: FichaPage,
});

type RespostaSaude = { valor: "sim" | "nao" | null; detalhe: string };

const PERGUNTAS_SAUDE: { id: string; label: string; detalhe?: string }[] = [
  { id: "alergias", label: "Tem alergias?", detalhe: "Quais? (medicamentos, tintas, iodo, látex etc.)" },
  { id: "tratamento", label: "Está em tratamento médico ou usa medicação contínua?", detalhe: "Qual medicação?" },
  { id: "diabetes", label: "Tem diabetes?", detalhe: "Tipo e se é controlada" },
  { id: "epilepsia", label: "Tem epilepsia ou histórico de convulsão?", detalhe: "Especifique" },
  { id: "cardiopatia", label: "Possui cardiopatia?" },
  { id: "hipertensao", label: "Possui hipo ou hipertensão arterial?" },
  { id: "coagulacao", label: "Tem problema de coagulação ou hemofilia?" },
  { id: "transmissivel", label: "Possui alguma doença transmissível pelo sangue?", detalhe: "Hepatite, HIV etc." },
  { id: "pele_regiao", label: "Tem alguma doença de pele na região a ser tatuada?", detalhe: "Psoríase, vitiligo, dermatite" },
  { id: "queloide", label: "Tem tendência a queloide ou cicatriz hipertrófica?" },
  { id: "gestante", label: "Está gestante ou amamentando?", detalhe: "Quantas semanas?" },
  { id: "fumante", label: "É fumante?" },
  { id: "alcool_drogas", label: "Consumiu álcool ou drogas nas últimas 24h?" },
  { id: "alimentou", label: "Alimentou-se bem nas últimas 4h?" },
  { id: "pele_bronzeada", label: "Está com a pele bronzeada ou usando ácido no local?" },
  { id: "reacao_anterior", label: "Já teve reação alérgica em tatuagem anterior?", detalhe: "Especifique" },
  { id: "anticoagulante", label: "Usa anticoagulante, aspirina ou anti-inflamatório recentemente?" },
  { id: "desmaio", label: "Já teve desmaio ou mal-estar em procedimento com agulha?" },
];

const GENEROS = ["Feminino", "Masculino", "Outro"];
const ESTADOS_CIVIS = ["Solteiro(a)", "Casado(a)", "Divorciado(a)", "Viúvo(a)", "União estável"];

function calcularIdade(dataStr: string): number | null {
  if (!dataStr) return null;
  const hoje = new Date();
  const nascimento = new Date(dataStr + "T00:00:00");
  if (isNaN(nascimento.getTime())) return null;
  let idade = hoje.getFullYear() - nascimento.getFullYear();
  const m = hoje.getMonth() - nascimento.getMonth();
  if (m < 0 || (m === 0 && hoje.getDate() < nascimento.getDate())) idade--;
  return idade;
}

function Progresso({ atual, total }: { atual: number; total: number }) {
  const pct = ((atual + 1) / total) * 100;
  return (
    <div className="mb-2">
      <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
        <span>Etapa {atual + 1} de {total}</span>
        <span>{Math.round(pct)}%</span>
      </div>
      <div className="h-1 bg-muted rounded-full overflow-hidden">
        <div className="h-full bg-primary transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ToggleSimNao({ valor, onChange }: { valor: "sim" | "nao" | null; onChange: (v: "sim" | "nao") => void }) {
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={() => onChange("sim")}
        className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
          valor === "sim" ? "bg-primary border-primary text-primary-foreground" : "bg-card border-input text-muted-foreground"
        }`}
      >
        Sim
      </button>
      <button
        type="button"
        onClick={() => onChange("nao")}
        className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
          valor === "nao" ? "bg-foreground border-foreground text-background" : "bg-card border-input text-muted-foreground"
        }`}
      >
        Não
      </button>
    </div>
  );
}

function Pergunta({
  pergunta,
  resposta,
  onChange,
}: {
  pergunta: { id: string; label: string; detalhe?: string };
  resposta: RespostaSaude | undefined;
  onChange: (r: RespostaSaude) => void;
}) {
  const r = resposta ?? { valor: null, detalhe: "" };
  const faltaDetalhe = r.valor === "sim" && !!pergunta.detalhe && !r.detalhe.trim();
  return (
    <div className="py-3 border-b border-border last:border-0">
      <p className="text-sm text-foreground mb-2 leading-snug">{pergunta.label} *</p>
      <ToggleSimNao valor={r.valor} onChange={(valor) => onChange({ ...r, valor })} />
      {r.valor === "sim" && pergunta.detalhe && (
        <Input
          placeholder={pergunta.detalhe}
          value={r.detalhe}
          onChange={(e) => onChange({ ...r, detalhe: e.target.value })}
          className={`mt-2 ${faltaDetalhe ? "border-destructive" : ""}`}
        />
      )}
    </div>
  );
}

interface DadosPessoais {
  nomeCompleto: string;
  nascimento: string;
  cpf: string;
  genero: string;
  estadoCivil: string;
  telefone: string;
  email: string;
  instagram: string;
  enderecoRua: string;
  enderecoBairro: string;
  enderecoCidadeEstado: string;
  enderecoCep: string;
  contatoEmergenciaNome: string;
  contatoEmergenciaTelefone: string;
}

interface DadosResponsavel {
  nome: string;
  cpf: string;
  nascimento: string;
  parentesco: string;
  endereco: string;
  cidadeEstado: string;
  autoriza: boolean;
}

interface DadosConsentimento {
  termo: boolean;
  imagem: boolean;
  nomeAssinatura: string;
}

type EtapaValidacao = "carregando" | "invalido" | "valido";

function FichaPage() {
  const { slug } = Route.useParams();

  const [etapaValidacao, setEtapaValidacao] = useState<EtapaValidacao>("carregando");
  const [erroValidacao, setErroValidacao] = useState<string | null>(null);
  const [nomeStudio, setNomeStudio] = useState<string | null>(null);

  const [step, setStep] = useState(0);
  const [enviado, setEnviado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const [cadastroEncontrado, setCadastroEncontrado] = useState(false);

  const [dados, setDados] = useState<DadosPessoais>({
    nomeCompleto: "",
    nascimento: "",
    cpf: "",
    genero: "",
    estadoCivil: "",
    telefone: "",
    email: "",
    instagram: "",
    enderecoRua: "",
    enderecoBairro: "",
    enderecoCidadeEstado: "",
    enderecoCep: "",
    contatoEmergenciaNome: "",
    contatoEmergenciaTelefone: "",
  });
  const [responsavel, setResponsavel] = useState<DadosResponsavel>({
    nome: "",
    cpf: "",
    nascimento: "",
    parentesco: "",
    endereco: "",
    cidadeEstado: "",
    autoriza: false,
  });
  const [saude, setSaude] = useState<Record<string, RespostaSaude>>({});
  const [tipoSanguineo, setTipoSanguineo] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [consentimento, setConsentimento] = useState<DadosConsentimento>({
    termo: false,
    imagem: false,
    nomeAssinatura: "",
  });

  useEffect(() => {
    let ativo = true;
    async function validar() {
      try {
        const { data, error } = await supabase.rpc("validar_slug_ficha", { p_slug: slug });
        if (!ativo) return;
        if (error) {
          setErroValidacao("Não foi possível verificar este link agora. Verifique sua conexão e tente novamente.");
          setEtapaValidacao("invalido");
          return;
        }
        const info = Array.isArray(data) ? data[0] : data;
        if (!info?.valido) {
          setErroValidacao("Este link de cadastro não é válido. Confira com o estúdio o link correto.");
          setEtapaValidacao("invalido");
          return;
        }
        setNomeStudio(info.nome_studio ?? null);
        setEtapaValidacao("valido");
      } catch {
        if (!ativo) return;
        setErroValidacao("Não foi possível verificar este link agora. Verifique sua conexão e tente novamente.");
        setEtapaValidacao("invalido");
      }
    }
    validar();
    return () => {
      ativo = false;
    };
  }, [slug]);

  // Busca cadastro existente pelo CPF (dentro do mesmo estúdio) pra evitar
  // duplicar o cliente — pré-preenche os dados, mas segue editável.
  useEffect(() => {
    const cpfDigitos = dados.cpf.replace(/\D/g, "");
    if (cpfDigitos.length !== 11) {
      setCadastroEncontrado(false);
      return;
    }
    let ativo = true;
    const timer = setTimeout(async () => {
      try {
        const { data, error } = await supabase.rpc("buscar_cliente_por_cpf", { p_slug: slug, p_cpf: dados.cpf });
        if (!ativo || error) return;
        const info = Array.isArray(data) ? data[0] : data;
        if (!info?.encontrado) {
          setCadastroEncontrado(false);
          return;
        }
        setCadastroEncontrado(true);
        setDados((prev) => ({
          ...prev,
          nomeCompleto: info.nome || prev.nomeCompleto,
          nascimento: info.data_nascimento || prev.nascimento,
          genero: info.genero || prev.genero,
          estadoCivil: info.estado_civil || prev.estadoCivil,
          telefone: info.telefone || prev.telefone,
          email: info.email || prev.email,
          instagram: info.instagram || prev.instagram,
          contatoEmergenciaNome: info.contato_emergencia_nome || prev.contatoEmergenciaNome,
          contatoEmergenciaTelefone: info.contato_emergencia_telefone || prev.contatoEmergenciaTelefone,
          enderecoRua: info.endereco_rua || prev.enderecoRua,
          enderecoBairro: info.endereco_bairro || prev.enderecoBairro,
          enderecoCidadeEstado: info.endereco_cidade_estado || prev.enderecoCidadeEstado,
          enderecoCep: info.endereco_cep || prev.enderecoCep,
        }));
      } catch {
        // busca é só conveniência — se falhar, segue o preenchimento normal
      }
    }, 600);
    return () => {
      ativo = false;
      clearTimeout(timer);
    };
  }, [dados.cpf, slug]);

  const idade = calcularIdade(dados.nascimento);
  const ehMenor = idade !== null && idade < 18;

  const idadeResponsavel = calcularIdade(responsavel.nascimento);
  const responsavelMenor = idadeResponsavel !== null && idadeResponsavel < 18;

  const steps = ehMenor ? ["dados", "responsavel", "saude", "consentimento"] : ["dados", "saude", "consentimento"];
  const etapaAtual = steps[Math.min(step, steps.length - 1)];
  const hoje = new Date().toLocaleDateString("pt-BR");

  function dadosValidos() {
    return (
      dados.nomeCompleto.trim() &&
      dados.nascimento &&
      dados.cpf.trim() &&
      dados.genero &&
      dados.estadoCivil &&
      dados.telefone.trim() &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dados.email.trim()) &&
      dados.instagram.trim() &&
      dados.enderecoRua.trim() &&
      dados.enderecoBairro.trim() &&
      dados.enderecoCidadeEstado.trim() &&
      dados.enderecoCep.trim() &&
      dados.contatoEmergenciaNome.trim() &&
      dados.contatoEmergenciaTelefone.trim()
    );
  }
  function responsavelValidos() {
    return (
      responsavel.nome.trim() &&
      responsavel.cpf.trim() &&
      responsavel.nascimento &&
      idadeResponsavel !== null &&
      idadeResponsavel >= 18 &&
      responsavel.parentesco.trim() &&
      responsavel.endereco.trim() &&
      responsavel.cidadeEstado.trim() &&
      responsavel.autoriza
    );
  }
  function saudeValidos() {
    return PERGUNTAS_SAUDE.every((p) => {
      const r = saude[p.id];
      if (!r || !r.valor) return false;
      if (r.valor === "sim" && p.detalhe && !r.detalhe?.trim()) return false;
      return true;
    });
  }
  function consentimentoValidos() {
    return consentimento.termo && consentimento.imagem && consentimento.nomeAssinatura.trim().length > 2;
  }

  function etapaValida() {
    if (etapaAtual === "dados") return dadosValidos();
    if (etapaAtual === "responsavel") return responsavelValidos();
    if (etapaAtual === "saude") return saudeValidos();
    if (etapaAtual === "consentimento") return consentimentoValidos();
    return false;
  }

  async function enviarFicha() {
    setErroEnvio(null);
    setEnviando(true);
    try {
      const respostasAnamnese = Object.fromEntries(
        Object.entries(saude).map(([id, r]) => [id, { valor: r.valor, detalhe: r.detalhe || null }])
      );

      const p_dados = {
        nome_completo: dados.nomeCompleto,
        cpf: dados.cpf,
        nascimento: dados.nascimento,
        genero: dados.genero,
        estado_civil: dados.estadoCivil,
        telefone: dados.telefone,
        email: dados.email,
        instagram: dados.instagram,
        endereco_rua: dados.enderecoRua,
        endereco_bairro: dados.enderecoBairro,
        endereco_cidade_estado: dados.enderecoCidadeEstado,
        endereco_cep: dados.enderecoCep,
        contato_emergencia_nome: dados.contatoEmergenciaNome,
        contato_emergencia_telefone: dados.contatoEmergenciaTelefone,
        eh_menor: ehMenor,
        responsavel_nome: ehMenor ? responsavel.nome : null,
        responsavel_cpf: ehMenor ? responsavel.cpf : null,
        responsavel_nascimento: ehMenor ? responsavel.nascimento : null,
        responsavel_parentesco: ehMenor ? responsavel.parentesco : null,
        responsavel_endereco: ehMenor ? responsavel.endereco : null,
        responsavel_cidade_estado: ehMenor ? responsavel.cidadeEstado : null,
        respostas_anamnese: respostasAnamnese,
        tipo_sanguineo: tipoSanguineo || null,
        observacoes: observacoes || null,
        aceite_uso_imagem: consentimento.imagem,
        aceite_termo_responsabilidade: consentimento.termo,
        assinatura_nome: consentimento.nomeAssinatura,
        assinatura_data: new Date().toISOString().slice(0, 10),
      };

      const { error } = await supabase.rpc("criar_cliente_via_ficha", { p_slug: slug, p_dados });

      if (error) {
        if (error.message?.toLowerCase().includes("link inválido") || error.message?.toLowerCase().includes("link invalido")) {
          setErroEnvio("Este link de cadastro não é mais válido. Confira com o estúdio o link correto.");
        } else {
          setErroEnvio("Não foi possível enviar sua ficha agora. Tente novamente em alguns instantes.");
        }
        return;
      }

      setEnviado(true);
    } catch {
      setErroEnvio("Falha de conexão. Verifique sua internet e tente enviar novamente.");
    } finally {
      setEnviando(false);
    }
  }

  function avancar() {
    if (!etapaValida()) return;
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      enviarFicha();
    }
  }
  function voltar() {
    if (step > 0) setStep(step - 1);
  }

  if (etapaValidacao === "carregando") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="size-12 rounded-full border-4 border-muted border-t-primary animate-spin" />
      </div>
    );
  }

  if (etapaValidacao === "invalido") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <AlertCircle className="mx-auto mb-4 text-destructive" size={40} />
          <h1 className="text-lg font-medium text-foreground mb-2">Link inválido</h1>
          <p className="text-sm text-muted-foreground leading-relaxed">{erroValidacao}</p>
        </div>
      </div>
    );
  }

  if (enviado) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md mx-auto px-5 py-16 text-center">
          <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-5 text-2xl">
            ✓
          </div>
          <h2 className="text-lg font-medium text-foreground mb-2">Cadastro criado</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Seu cadastro e sua ficha de anamnese foram salvos no sistema do estúdio.
            O tatuador já pode marcar sua sessão.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-md mx-auto px-5 py-8">
        <div className="mb-1">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">{nomeStudio ?? "Estúdio"}</p>
          <h1 className="text-base font-medium text-foreground">Cadastro e ficha de anamnese</h1>
        </div>
        <p className="text-xs text-muted-foreground mb-5 leading-relaxed">
          Preencha seus dados para criar seu cadastro no estúdio. Todos os campos são obrigatórios, exceto quando indicado.
        </p>

        <Progresso atual={step} total={steps.length} />

        {etapaAtual === "dados" && (
          <div className="mt-4">
            <h2 className="text-sm font-medium text-foreground mb-4 uppercase tracking-wide">Dados pessoais</h2>

            <div className="mb-4">
              <Label className="mb-1.5 block">Nome completo *</Label>
              <Input
                placeholder="NOME COMPLETO"
                value={dados.nomeCompleto}
                onChange={(e) => setDados({ ...dados, nomeCompleto: e.target.value.toUpperCase() })}
              />
            </div>

            <div className="mb-1">
              <Label className="mb-1.5 block">Data de nascimento *</Label>
              <DatePicker
                value={dados.nascimento}
                onChange={(v) => setDados({ ...dados, nascimento: v })}
                comSeletorDeAno
              />
            </div>
            {idade !== null && <p className="text-xs text-muted-foreground mb-4">Idade: {idade} anos</p>}
            {idade === null && <div className="mb-4" />}

            {ehMenor && (
              <div className="mb-4 flex items-start gap-2.5 bg-destructive/10 border border-destructive/30 rounded-lg px-3.5 py-3">
                <AlertCircle className="text-destructive shrink-0 mt-0.5" size={18} />
                <div>
                  <p className="text-sm font-semibold text-destructive">Cliente menor de idade</p>
                  <p className="text-xs text-destructive/90 mt-0.5 leading-relaxed">
                    Na próxima etapa, os dados do responsável legal serão solicitados para autorizar o procedimento.
                  </p>
                </div>
              </div>
            )}

            <div className="mb-4">
              <Label className="mb-1.5 block">CPF *</Label>
              <Input
                placeholder="000.000.000-00"
                value={dados.cpf}
                onChange={(e) => setDados({ ...dados, cpf: e.target.value })}
              />
            </div>

            {cadastroEncontrado && (
              <div className="mb-4 flex items-start gap-2.5 bg-primary/10 border border-primary/30 rounded-lg px-3.5 py-3">
                <div className="size-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 mt-0.5 text-[10px]">✓</div>
                <p className="text-xs text-foreground leading-relaxed">
                  Encontramos seu cadastro! Conferimos seus dados abaixo — pode atualizar o que tiver mudado.
                </p>
              </div>
            )}

            <div className="mb-4">
              <Label className="mb-1.5 block">Gênero *</Label>
              <Select value={dados.genero} onValueChange={(v) => setDados({ ...dados, genero: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {GENEROS.map((g) => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="mb-4">
              <Label className="mb-1.5 block">Estado civil *</Label>
              <Select value={dados.estadoCivil} onValueChange={(v) => setDados({ ...dados, estadoCivil: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {ESTADOS_CIVIS.map((e) => (
                    <SelectItem key={e} value={e}>{e}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="mb-4">
              <Label className="mb-1.5 block">Telefone com DDD *</Label>
              <Input
                placeholder="(11) 91234-5678"
                value={dados.telefone}
                onChange={(e) => setDados({ ...dados, telefone: e.target.value })}
              />
            </div>

            <div className="mb-4">
              <Label className="mb-1.5 block">E-mail *</Label>
              <Input
                type="email"
                placeholder="seu@email.com"
                value={dados.email}
                onChange={(e) => setDados({ ...dados, email: e.target.value })}
              />
            </div>

            <div className="mb-4">
              <Label className="mb-1.5 block">Instagram *</Label>
              <Input
                placeholder="@seuusuario"
                value={dados.instagram}
                onChange={(e) => setDados({ ...dados, instagram: e.target.value })}
              />
            </div>

            <div className="mb-4">
              <Label className="mb-1.5 block">Rua e número *</Label>
              <Input
                placeholder="Rua das Flores, 123"
                value={dados.enderecoRua}
                onChange={(e) => setDados({ ...dados, enderecoRua: e.target.value })}
              />
            </div>

            <div className="mb-4">
              <Label className="mb-1.5 block">Bairro *</Label>
              <Input
                placeholder="Centro"
                value={dados.enderecoBairro}
                onChange={(e) => setDados({ ...dados, enderecoBairro: e.target.value })}
              />
            </div>

            <div className="mb-4">
              <Label className="mb-1.5 block">Cidade / Estado *</Label>
              <Input
                placeholder="São Paulo/SP"
                value={dados.enderecoCidadeEstado}
                onChange={(e) => setDados({ ...dados, enderecoCidadeEstado: e.target.value })}
              />
            </div>

            <div className="mb-4">
              <Label className="mb-1.5 block">CEP *</Label>
              <Input
                placeholder="00000-000"
                value={dados.enderecoCep}
                onChange={(e) => setDados({ ...dados, enderecoCep: e.target.value })}
              />
            </div>

            <div className="mb-4">
              <Label className="mb-1.5 block">Contato de emergência — nome *</Label>
              <Input
                value={dados.contatoEmergenciaNome}
                onChange={(e) => setDados({ ...dados, contatoEmergenciaNome: e.target.value })}
              />
            </div>

            <div className="mb-4">
              <Label className="mb-1.5 block">Contato de emergência — telefone *</Label>
              <Input
                placeholder="(11) 91234-5678"
                value={dados.contatoEmergenciaTelefone}
                onChange={(e) => setDados({ ...dados, contatoEmergenciaTelefone: e.target.value })}
              />
            </div>
          </div>
        )}

        {etapaAtual === "responsavel" && (
          <div className="mt-4">
            <h2 className="text-sm font-medium text-foreground mb-2 uppercase tracking-wide">Autorização do responsável</h2>
            <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
              Como o cliente é menor de idade, o responsável legal precisa preencher e autorizar o procedimento.
            </p>

            <div className="mb-4">
              <Label className="mb-1.5 block">Nome do responsável *</Label>
              <Input
                value={responsavel.nome}
                onChange={(e) => setResponsavel({ ...responsavel, nome: e.target.value.toUpperCase() })}
              />
            </div>
            <div className="mb-4">
              <Label className="mb-1.5 block">CPF do responsável *</Label>
              <Input
                placeholder="000.000.000-00"
                value={responsavel.cpf}
                onChange={(e) => setResponsavel({ ...responsavel, cpf: e.target.value })}
              />
            </div>
            <div className="mb-1">
              <Label className="mb-1.5 block">Data de nascimento do responsável *</Label>
              <DatePicker
                value={responsavel.nascimento}
                onChange={(v) => setResponsavel({ ...responsavel, nascimento: v })}
                comSeletorDeAno
              />
            </div>
            {idadeResponsavel !== null && (
              <p className={`text-xs mb-4 ${responsavelMenor ? "text-destructive font-medium" : "text-muted-foreground"}`}>
                Idade: {idadeResponsavel} anos
              </p>
            )}
            {idadeResponsavel === null && <div className="mb-4" />}
            {responsavelMenor && (
              <div className="mb-4 flex items-start gap-2.5 bg-destructive/10 border border-destructive/30 rounded-lg px-3.5 py-3">
                <AlertCircle className="text-destructive shrink-0 mt-0.5" size={18} />
                <p className="text-xs text-destructive leading-relaxed">
                  O responsável legal precisa ser maior de 18 anos para autorizar o procedimento. Não é possível continuar com a data informada.
                </p>
              </div>
            )}
            <div className="mb-4">
              <Label className="mb-1.5 block">Grau de parentesco ou representação legal *</Label>
              <Input
                placeholder="Ex: Mãe, pai, tutor(a) legal"
                value={responsavel.parentesco}
                onChange={(e) => setResponsavel({ ...responsavel, parentesco: e.target.value })}
              />
            </div>
            <div className="mb-4">
              <Label className="mb-1.5 block">Endereço do responsável *</Label>
              <Input
                value={responsavel.endereco}
                onChange={(e) => setResponsavel({ ...responsavel, endereco: e.target.value })}
              />
            </div>
            <div className="mb-4">
              <Label className="mb-1.5 block">Cidade / estado *</Label>
              <Input
                value={responsavel.cidadeEstado}
                onChange={(e) => setResponsavel({ ...responsavel, cidadeEstado: e.target.value })}
              />
            </div>

            <div className="bg-muted/50 border border-border rounded-lg p-3 text-xs text-muted-foreground leading-relaxed mb-3 space-y-2.5">
              <p>
                Autorizo o estúdio {nomeStudio ?? ""} a realizar o procedimento de tatuagem descrito, assumindo plena
                responsabilidade como responsável legal pelo menor.
              </p>
              <p>
                <span className="font-medium text-foreground">Acompanhamento presencial:</span> declaro que estarei
                presente no estúdio no momento da assinatura e durante toda a realização do procedimento.
              </p>
              <p>
                <span className="font-medium text-foreground">Ciente dos riscos e cuidados:</span> fui devidamente
                informado(a) pelo profissional sobre o caráter permanente da tatuagem, os riscos inerentes (dor,
                inchaço, possibilidade de alergias ou complicações cicatriciais) e os procedimentos de higiene
                necessários.
              </p>
              <p>
                <span className="font-medium text-foreground">Co-responsabilidade nos cuidados pós-procedimento:</span>{" "}
                assumo a responsabilidade de supervisionar e garantir o cumprimento de todos os cuidados recomendados
                (higienização, aplicação de produtos indicados, restrição de exposição ao sol, mar, piscina e saunas
                durante o período de cicatrização).
              </p>
            </div>
            <label className="flex gap-2.5 items-start text-sm text-foreground">
              <Checkbox
                className="mt-0.5"
                checked={responsavel.autoriza}
                onCheckedChange={(v) => setResponsavel({ ...responsavel, autoriza: v === true })}
              />
              <span>Li e autorizo, na qualidade de responsável legal, nos termos acima. *</span>
            </label>
          </div>
        )}

        {etapaAtual === "saude" && (
          <div className="mt-4">
            <h2 className="text-sm font-medium text-foreground mb-1 uppercase tracking-wide">Histórico de saúde</h2>
            <p className="text-xs text-muted-foreground mb-3">
              Responda com atenção — essas informações protegem sua saúde durante o procedimento.
            </p>
            <div>
              {PERGUNTAS_SAUDE.map((p) => (
                <Pergunta
                  key={p.id}
                  pergunta={p}
                  resposta={saude[p.id]}
                  onChange={(valor) => setSaude({ ...saude, [p.id]: valor })}
                />
              ))}
            </div>
            <div className="mt-3 mb-4">
              <Label className="mb-1.5 block">Tipo sanguíneo e fator RH (se souber)</Label>
              <Input placeholder="Ex: O+" value={tipoSanguineo} onChange={(e) => setTipoSanguineo(e.target.value)} />
            </div>
            <div>
              <Label className="mb-1.5 block">Algo mais que julgue importante informar? (opcional)</Label>
              <Textarea rows={3} value={observacoes} onChange={(e) => setObservacoes(e.target.value)} />
            </div>
          </div>
        )}

        {etapaAtual === "consentimento" && (
          <div className="mt-4">
            <h2 className="text-sm font-medium text-foreground mb-4 uppercase tracking-wide">Consentimento</h2>

            <div className="bg-muted/50 border border-border rounded-lg p-3.5 text-xs text-muted-foreground leading-relaxed mb-4 max-h-64 overflow-y-auto space-y-3">
              <div>
                <p className="font-medium text-foreground mb-1">Esclarecimento sobre riscos e procedimento</p>
                <p>Declaro que compreendo e aceito que:</p>
                <ul className="list-disc pl-4 mt-1 space-y-1">
                  <li>A tatuagem é um procedimento definitivo e a remoção a laser é dispendiosa, dolorosa e pode não restaurar a pele ao estado original;</li>
                  <li>O processo envolve o uso de agulhas e pode causar dor, inchaço e vermelhidão temporários;</li>
                  <li>Existem riscos inerentes de infecção, alergia tardia aos pigmentos ou reações cicatriciais (como queloides);</li>
                  <li>O resultado final depende do processo de cicatrização e da observância estrita dos cuidados pós-procedimento.</li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">Aprovação da arte e do local</p>
                <p>Declaro que aprovei a arte (decalque), o tamanho, a grafia (textos/datas) e a posição no corpo antes do início da aplicação da tinta.</p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">Ciência dos cuidados pós-tatuagem</p>
                <p>
                  Confirmo ter recebido (verbalmente e por escrito) o guia de cuidados posteriores (higienização, uso
                  de pomada, proteção solar, não coçar e não frequentar praias/piscinas durante a cicatrização). Fica
                  registrado que o descumprimento dessas instruções isenta o tatuador de responsabilidade sobre
                  falhas ou infecções decorrentes de má higienização.
                </p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">Proteção de dados (LGPD)</p>
                <p>Os dados pessoais fornecidos serão mantidos em sigilo e usados exclusivamente para controle sanitário e de atendimento do estúdio.</p>
              </div>
            </div>

            <label className="flex gap-2.5 items-start mb-4 text-sm text-foreground">
              <Checkbox
                className="mt-0.5"
                checked={consentimento.termo}
                onCheckedChange={(v) => setConsentimento({ ...consentimento, termo: v === true })}
              />
              <span>Li, entendi e concordo com os termos acima. *</span>
            </label>

            <label className="flex gap-2.5 items-start mb-5 text-sm text-foreground">
              <Checkbox
                className="mt-0.5"
                checked={consentimento.imagem}
                onCheckedChange={(v) => setConsentimento({ ...consentimento, imagem: v === true })}
              />
              <span>Autorizo o registro fotográfico/vídeo do procedimento para divulgação do profissional. *</span>
            </label>

            <div className="mb-1">
              <Label className="mb-1.5 block">
                Assinatura (nome completo) * {!consentimento.termo && <span className="text-muted-foreground font-normal">— disponível após aceitar os termos acima</span>}
              </Label>
              <Input
                disabled={!consentimento.termo}
                value={consentimento.nomeAssinatura}
                onChange={(e) => setConsentimento({ ...consentimento, nomeAssinatura: e.target.value.toUpperCase() })}
              />
            </div>
            <p className="text-xs text-muted-foreground">Data: {hoje}</p>
          </div>
        )}

        {erroEnvio && (
          <div className="mt-5 flex items-start gap-2.5 bg-destructive/10 border border-destructive/30 rounded-lg px-3.5 py-3">
            <AlertCircle className="text-destructive shrink-0 mt-0.5" size={18} />
            <p className="text-sm text-destructive">{erroEnvio}</p>
          </div>
        )}

        <div className="flex gap-3 mt-8">
          {step > 0 && (
            <Button type="button" variant="outline" className="flex-1" onClick={voltar} disabled={enviando}>
              Voltar
            </Button>
          )}
          <Button type="button" className="flex-1" onClick={avancar} disabled={!etapaValida() || enviando}>
            {enviando ? "Enviando..." : etapaAtual === "consentimento" ? "Enviar ficha" : "Continuar"}
          </Button>
        </div>
      </div>
    </div>
  );
}
